export const STOCK_IN_REQUEST_STATUS = {
	Draft: "draft",
	Submitted: "submitted",
	Approved: "approved",
	Rejected: "rejected",
	Cancelled: "cancelled",
} as const;

export type StockInRequestStatus =
	(typeof STOCK_IN_REQUEST_STATUS)[keyof typeof STOCK_IN_REQUEST_STATUS];

export const RECEIVE_REASON = {
	Match: "match",
	Shortage: "shortage",
	Damaged: "damaged",
	Expired: "expired",
} as const;

export type ReceiveReason =
	(typeof RECEIVE_REASON)[keyof typeof RECEIVE_REASON];

export const ATTACHMENT_KIND = { Image: "img", Pdf: "pdf" } as const;

export type AttachmentKind =
	(typeof ATTACHMENT_KIND)[keyof typeof ATTACHMENT_KIND];

export type StockInAttachment = {
	fileName: string;
	attachmentKind: AttachmentKind;
	fileSizeText: string;
};

export type StockInRequestLineRecord = {
	productCode: string;
	requestedQuantity: number;
	expiryDate: string;
	receivedQuantity?: number;
	receiveReason?: ReceiveReason;
	storeNote?: string;
};

export type StockInRequestRecord = {
	stockInRequestId: string;
	stockInRequestNumber: string;
	storeId: string;
	estimatedDeliveryDate: string;
	submittedAt: string;
	status: StockInRequestStatus;
	isStoreInactive?: boolean;
	note: string;
	attachments: StockInAttachment[];
	rejectionReason?: string;
	cancellationReason?: string;
	stockInNumber?: string;
	stockOutNumber?: string;
	lines: StockInRequestLineRecord[];
};

// Sample numbers are fictional.
export const SAMPLE_STOCK_IN_REQUESTS: StockInRequestRecord[] = [
	{
		stockInRequestId: "019a3f1c-7e21-7b42-8d01-6c9f5e32b001",
		stockInRequestNumber: "CSIR/261012/58203914",
		storeId: "store-kokas",
		estimatedDeliveryDate: "2026-10-13",
		submittedAt: "2026-10-12T09:12:00",
		status: STOCK_IN_REQUEST_STATUS.Submitted,
		note: "Barang datang sekitar pukul 10.00.",
		attachments: [
			{
				fileName: "foto-produk-dus.jpg",
				attachmentKind: ATTACHMENT_KIND.Image,
				fileSizeText: "820 KB",
			},
		],
		lines: [
			{
				productCode: "899743540208",
				requestedQuantity: 12,
				expiryDate: "2027-04-10",
			},
			{
				productCode: "699170286139",
				requestedQuantity: 6,
				expiryDate: "2027-02-01",
			},
			{
				productCode: "281172683728",
				requestedQuantity: 12,
				expiryDate: "2027-01-20",
			},
		],
	},
	{
		stockInRequestId: "019a3f1c-7e21-7b42-8d02-6c9f5e32b002",
		stockInRequestNumber: "CSIR/261011/39857206",
		storeId: "store-lm-nusantara",
		estimatedDeliveryDate: "",
		submittedAt: "",
		status: STOCK_IN_REQUEST_STATUS.Draft,
		note: "",
		attachments: [],
		lines: [
			{ productCode: "151462779163", requestedQuantity: 6, expiryDate: "" },
		],
	},
	{
		stockInRequestId: "019a3f1c-7e21-7b42-8d03-6c9f5e32b003",
		stockInRequestNumber: "CSIR/261010/70528341",
		storeId: "store-kokas",
		estimatedDeliveryDate: "2026-10-11",
		submittedAt: "2026-10-10T16:20:00",
		status: STOCK_IN_REQUEST_STATUS.Cancelled,
		cancellationReason: "Salah pilih toko.",
		note: "",
		attachments: [],
		lines: [
			{
				productCode: "356198238414",
				requestedQuantity: 6,
				expiryDate: "2027-03-12",
			},
		],
	},
	{
		stockInRequestId: "019a3f1c-7e21-7b42-8d04-6c9f5e32b004",
		stockInRequestNumber: "CSIR/261008/27461093",
		storeId: "store-scbd",
		estimatedDeliveryDate: "2026-10-08",
		submittedAt: "2026-10-07T14:40:00",
		status: STOCK_IN_REQUEST_STATUS.Approved,
		stockInNumber: "CSI/261008/80417735",
		stockOutNumber: "CSO/261008/93120584",
		note: "",
		attachments: [
			{
				fileName: "surat-jalan-scbd.pdf",
				attachmentKind: ATTACHMENT_KIND.Pdf,
				fileSizeText: "214 KB",
			},
		],
		lines: [
			{
				productCode: "574101949307",
				requestedQuantity: 10,
				expiryDate: "2027-03-01",
				receivedQuantity: 10,
				receiveReason: RECEIVE_REASON.Match,
			},
			{
				productCode: "134759234543",
				requestedQuantity: 12,
				expiryDate: "2027-05-15",
				receivedQuantity: 11,
				receiveReason: RECEIVE_REASON.Shortage,
				storeNote: "Satu bungkus tidak ada di dus.",
			},
		],
	},
	{
		stockInRequestId: "019a3f1c-7e21-7b42-8d05-6c9f5e32b005",
		stockInRequestNumber: "CSIR/261003/66310428",
		storeId: "store-lm-nusantara",
		estimatedDeliveryDate: "2026-10-03",
		submittedAt: "2026-10-02T10:05:00",
		status: STOCK_IN_REQUEST_STATUS.Approved,
		stockInNumber: "CSI/261003/41275096",
		note: "",
		attachments: [],
		lines: [
			{
				productCode: "623352604864",
				requestedQuantity: 15,
				expiryDate: "2027-01-30",
				receivedQuantity: 15,
				receiveReason: RECEIVE_REASON.Match,
			},
			{
				productCode: "355026730862",
				requestedQuantity: 8,
				expiryDate: "2026-12-20",
				receivedQuantity: 8,
				receiveReason: RECEIVE_REASON.Match,
			},
		],
	},
	{
		stockInRequestId: "019a3f1c-7e21-7b42-8d06-6c9f5e32b006",
		stockInRequestNumber: "CSIR/260929/15094672",
		storeId: "store-lm-nusantara",
		estimatedDeliveryDate: "2026-09-30",
		submittedAt: "2026-09-29T10:05:00",
		status: STOCK_IN_REQUEST_STATUS.Rejected,
		rejectionReason:
			"Label barcode tidak terbaca saat diterima. Cetak ulang label, lalu ajukan lagi.",
		note: "",
		attachments: [],
		lines: [
			{
				productCode: "165782751746",
				requestedQuantity: 8,
				expiryDate: "2027-02-14",
			},
		],
	},
];
